import { useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import type { NombreIcono } from './iconos';
import estilos from './Aviso.module.css';

export type TonoAviso = 'info' | 'atencion' | 'peligro' | 'exito';

const ICONO_POR_TONO: Readonly<Record<TonoAviso, NombreIcono>> = {
  info: 'info',
  atencion: 'advertenciaContorno',
  peligro: 'errorContorno',
  exito: 'exitoContorno',
};

interface AvisoProps {
  children: ReactNode;
  tono?: TonoAviso;
  /** Reemplaza el icono que corresponde al tono. */
  icono?: NombreIcono;
  /**
   * Resumen de una o dos palabras para el móvil, donde el aviso se reduce a un
   * botón. Sin él sólo se ve el icono.
   */
  resumen?: string;
  className?: string;
}

/**
 * Aviso informativo de una pantalla.
 *
 * En pantalla ancha es una banda con su icono y su texto. En el móvil se
 * encoge a una pastilla que se despliega al tocarla: estos avisos explican algo
 * que conviene saber una vez, y dejarlos ocupando tres líneas fijas por encima
 * de los datos —a veces varios a la vez— empuja fuera de la pantalla aquello a
 * lo que se venía.
 *
 * El icono y el color se mantienen siempre visibles, así que el aviso sigue
 * anunciándose aunque el texto esté recogido.
 */
export function Aviso({ children, tono = 'info', icono, resumen, className }: AvisoProps) {
  const [desplegado, setDesplegado] = useState(false);

  const clases = [estilos.aviso, estilos[tono], desplegado ? estilos.desplegado : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={clases}>
      {/*
        El botón sólo existe como tal en móvil: en escritorio el CSS lo deja
        sin adornos de botón, porque ahí no hay nada que desplegar.
      */}
      <button
        type="button"
        className={estilos.cabecera}
        onClick={() => setDesplegado((v) => !v)}
        aria-expanded={desplegado}
      >
        <Icon nombre={icono ?? ICONO_POR_TONO[tono]} tamano={16} />
        {resumen && <span className={estilos.resumen}>{resumen}</span>}
      </button>

      <div className={estilos.texto}>{children}</div>
    </div>
  );
}

/**
 * Fila de avisos.
 *
 * Puestos uno al lado de otro ocupan una sola línea en el móvil en vez de una
 * banda cada uno.
 */
export function Avisos({ children }: { children: ReactNode }) {
  return <div className={estilos.fila}>{children}</div>;
}
